// ============================================
// 设备数据中枢（阶段2｜G1）：2D/3D 同源消费的设备实时数据 Store
// 数据来源：后端 IotGateway /ws/iot 推送（device:status / device:event / device:log 等）或本地模拟；
// 消费方：2D 组件（useWidgetData device 类型/设备表格/告警列表）与 3D 模型（绑定/悬浮标签/告警闪烁）读同一份数据。
// 原则：只做"语义归一"（deviceId → points/alarmList/online），不做业务过滤；数据同源保证 2D/3D 数值一致。
// ============================================

import { create } from 'zustand';

export interface DeviceRealData {
  deviceId: string;
  /** 实时点位：pointKey → number | boolean | string */
  points: Record<string, number | boolean | string>;
  /** 活跃告警 key 列表 */
  alarmList: string[];
  online: boolean;
  timestamp: number;
  lastTopic?: string;
}

/** AI 检测事件（M3-M7 联动：AI_DETECTION → 画中画调流） */
export interface AiDetectionEvent {
  deviceId: string;
  count: number;
  fps: number;
  model?: string;
  severity?: string;
  ts: number;
  raw?: string;
  /** M8.5 A-07：服务端已投影的 3D 地面目标（scenePos，m），供大屏空间高亮 */
  sceneTargets?: SceneTargetLite[];
}

/** M8.5：单个空间目标（服务端 scenePos 归一） */
export interface SceneTargetLite {
  x: number;
  z: number;
  className?: string;
  confidence?: number;
}

interface DeviceDataState {
  deviceMap: Record<string, DeviceRealData>;
  /** 全局选中设备（联动源：3D 点击 → 2D 过滤；2D 选中 → 3D 高亮/飞行） */
  selectedDeviceId: string | null;
  /** 最近一次选中是否带镜头聚焦（阶段3：locate3dDevice 带飞行 vs highlight3dDevice 不带） */
  selectedDeviceFocus: boolean;
  /** 选中动作自增计数（联动侧订阅触发，避免 selectedDeviceId 重复值漏触发） */
  deviceActionTick: number;
  setDeviceRealData: (payload: DeviceRealData) => void;
  /** 合并点位更新（不覆盖未变化点位） */
  upsertDevicePoints: (
    deviceId: string,
    points: Record<string, number | boolean | string>,
    meta?: Partial<Omit<DeviceRealData, 'deviceId' | 'points'>>
  ) => void;
  removeDevice: (deviceId: string) => void;
  /** 全局设备选中（联动源）。opts.focus=false 时仅选中不高亮/飞行（阶段3：highlight vs locate 区分） */
  selectDevice: (deviceId: string | null, opts?: { focus?: boolean }) => void;
  getDevice: (deviceId: string) => DeviceRealData | undefined;
  /** 由 deviceSocket 调用的消息归一入口（含离线/告警/点位解析） */
  mergeMessage: (topic: string, rawMessage: string) => void;
  /** 清空（测试/登出用） */
  reset: () => void;
  /** AI 检测事件流（AI_DETECTION，最新在前，上限 20）——M3-M7 画中画联动数据源 */
  aiEvents: AiDetectionEvent[];
}

/** 消息中的元数据字段（不当作点位） */
const META_KEYS = new Set([
  'deviceId', 'imei', 'id', 'sn', 'ts', 'timestamp', 'time', 'topic', 'type',
  'status', 'online', 'onlineStatus', 'alarmList', 'alarms', 'alarm', 'alarmCode',
  'event', 'message', 'msg', 'level', 'code', 'action', 'direction', '_raw', 'device',
  // 契约 v1.0 消息信封字段（非业务点位）：msg_id=消息序号
  'msg_id',
]);

/** 从消息 JSON 提取标量点位（排除元数据/嵌套对象/数组） */
export function pointsFromMessage(msg: Record<string, unknown>): Record<string, number | boolean | string> {
  const points: Record<string, number | boolean | string> = {};
  for (const [k, v] of Object.entries(msg)) {
    if (META_KEYS.has(k)) continue;
    if (v === null || v === undefined) continue;
    if (typeof v === 'object') continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    points[k] = v as number | boolean | string;
  }
  return points;
}

/** 从消息提取设备 ID（业务标识优先：imei，其次 deviceId / id / sn）。
 *  约定：设备数据中枢的运行键 = 设备注册表（/iot/devices）返回的 deviceId（RTU 即 imei），
 *  因为用户可见/可选的标识是 imei，而 UUID 是内部主键且设备重注册会变化。 */
export function deviceIdFromMessage(msg: Record<string, unknown>): string {
  return String(msg.imei || msg.deviceId || msg.id || msg.sn || '').trim();
}

/** 判定消息中的在线状态（默认 true，离线值/online=false 视为离线） */
export function onlineFromMessage(msg: Record<string, unknown>): boolean {
  if (typeof msg.online === 'boolean') return msg.online;
  const st = String(msg.status ?? msg.onlineStatus ?? '').toLowerCase();
  if (['offline', '0', 'false', 'disconnected', 'timeout'].includes(st)) return false;
  return true;
}

/** 深度扁平化标量叶节点：{cellular:{connected:true}} → {'network.cellular.connected':true}（数组/对象跳过） */
export function flattenScalarLeaves(value: unknown, prefix = ''): Record<string, number | boolean | string> {
  const out: Record<string, number | boolean | string> = {};
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return out;
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'object') {
      Object.assign(out, flattenScalarLeaves(v, key));
    } else if (typeof v === 'number' || typeof v === 'boolean' || (typeof v === 'string' && v.trim() !== '')) {
      out[key] = v;
    }
  }
  return out;
}

const emptyDevice = (deviceId: string): DeviceRealData => ({
  deviceId, points: {}, alarmList: [], online: true, timestamp: 0,
});

export const useDeviceDataStore = create<DeviceDataState>((set, get) => ({
  deviceMap: {},
  selectedDeviceId: null,
  selectedDeviceFocus: true,
  aiEvents: [],
  deviceActionTick: 0,

  setDeviceRealData: (payload) => {
    set((s) => ({ deviceMap: { ...s.deviceMap, [payload.deviceId]: payload } }));
  },

  upsertDevicePoints: (deviceId, points, meta) => {
    if (!deviceId) return;
    set((s) => {
      const prev = s.deviceMap[deviceId] || emptyDevice(deviceId);
      return {
        deviceMap: {
          ...s.deviceMap,
          [deviceId]: {
            ...prev,
            ...meta,
            deviceId,
            online: meta?.online ?? prev.online,
            timestamp: meta?.timestamp ?? Date.now(),
            points: { ...prev.points, ...points },
          },
        },
      };
    });
  },

  removeDevice: (deviceId) => {
    set((s) => {
      const { [deviceId]: _removed, ...rest } = s.deviceMap;
      return {
        deviceMap: rest,
        selectedDeviceId: s.selectedDeviceId === deviceId ? null : s.selectedDeviceId,
      };
    });
  },

  selectDevice: (deviceId, opts) =>
    set((s) => ({
      selectedDeviceId: deviceId,
      selectedDeviceFocus: opts?.focus ?? true,
      deviceActionTick: (s.deviceActionTick ?? 0) + 1,
    })),

  getDevice: (deviceId) => get().deviceMap[deviceId],

  mergeMessage: (topic, rawMessage) => {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(rawMessage);
    } catch {
      return; // 非 JSON 消息不参与设备数据归一
    }
    if (!msg || typeof msg !== 'object' || Array.isArray(msg)) return;
    const deviceId = deviceIdFromMessage(msg);
    if (!deviceId) return;
    const now = Date.now();
    const cur = get().deviceMap[deviceId];
    const points = pointsFromMessage(msg);

    if (topic === 'device:status') {
      const alarmList = msg.alarmList || msg.alarms || [];
      // 嵌套点位扁平化（真实 RTU status 的 network/io 为子对象；阶段8 板卡 status 的 regs）：
      // {network:{cellular:{connected,rssiDbm}}, io:{di0,do0,ai0}, regs:{0:92}} → network.cellular.connected / io.ai0 / regs.0 ...
      const points = {
        ...pointsFromMessage(msg),
        ...flattenScalarLeaves(msg.network, 'network'),
        ...flattenScalarLeaves(msg.io, 'io'),
        ...flattenScalarLeaves(msg.protocols, 'protocols'),
        ...flattenScalarLeaves(msg.regs, 'regs'),
        // 契约 v1.0 数据面：points 键=业务 key（平台不硬编码），扁平化为 points.<key>
        ...flattenScalarLeaves(msg.points, 'points'),
      };
      set((s) => ({
        deviceMap: {
          ...s.deviceMap,
          [deviceId]: {
            ...(cur || emptyDevice(deviceId)),
            deviceId,
            online: onlineFromMessage(msg),
            timestamp: now,
            lastTopic: topic,
            alarmList: Array.isArray(alarmList) ? alarmList.map(String) : cur?.alarmList || [],
            points: { ...(cur?.points || {}), ...points },
          },
        },
      }));
      return;
    }

    if (topic === 'device:event') {
      // AI 检测事件（AI_DETECTION，module=ai_camera）→ aiEvents 流（M3-M7 画中画联动）
      const evCode = String(msg.eventCode ?? msg.event ?? msg.type ?? '');
      if (evCode === 'AI_DETECTION') {
        const detail = (msg.detail ?? {}) as Record<string, unknown>;
        // M8.5 A-07：提取服务端已投影的 scenePos（地面单应），供 3D 空间高亮
        const dets = Array.isArray(detail.detections) ? (detail.detections as Record<string, unknown>[]) : [];
        const sceneTargets: SceneTargetLite[] = dets
          .map((d) => {
            const sp = d?.scenePos as { x?: unknown; z?: unknown } | undefined;
            if (!sp || typeof sp.x !== 'number' || typeof sp.z !== 'number') return null;
            return {
              x: sp.x,
              z: sp.z,
              className: typeof d.class_name === 'string' ? d.class_name : undefined,
              confidence: typeof d.confidence === 'number' ? d.confidence : undefined,
            } as SceneTargetLite;
          })
          .filter((t): t is SceneTargetLite => t !== null);
        const aiEvt: AiDetectionEvent = {
          deviceId,
          count: Number(detail.count ?? 0),
          fps: Number(detail.fps ?? 0),
          model: typeof detail.model === 'string' ? detail.model : undefined,
          severity: typeof msg.severity === 'string' ? msg.severity : 'info',
          ts: now,
          sceneTargets: sceneTargets.length ? sceneTargets : undefined,
        };
        set((s) => ({ aiEvents: [aiEvt, ...s.aiEvents].slice(0, 20) }));
      }
      // 告警事件 → 追加 alarmList；告警解除事件（clear/恢复）→ 移除/清空；普通事件 → 并入点位
      // 判定（阶段6 实测补强）：消息含 alarm 字段 或 事件码/severity 具告警特征
      // （后端广播形态：{eventCode, severity:'error', detail}，实测不含 alarm 字段）
      const severity = String(msg.severity ?? '').toLowerCase();
      const eventCode = String(msg.eventCode ?? msg.event ?? msg.type ?? '');
      const isClear =
        ['clear', 'ok', 'recovered', 'normal', 'resolved', 'ack'].includes(severity) ||
        /clear|resolved|recover|ack|解除|恢复|正常/.test(eventCode);
      if (isClear) {
        // 解除语义：明确告警标识（alarm/alarmCode/code）→ 移除该项；无标识 → 全清
        const entry = String(msg.alarm ?? msg.alarmCode ?? msg.code ?? '');
        const prev = cur?.alarmList || [];
        const next = entry
          ? prev.filter((a) => a !== entry)
          : [];
        set((s) => ({
          deviceMap: {
            ...s.deviceMap,
            [deviceId]: {
              ...(cur || emptyDevice(deviceId)),
              deviceId,
              online: cur?.online ?? true,
              timestamp: now,
              lastTopic: topic,
              alarmList: next,
              points: { ...(cur?.points || {}), ...points },
            },
          },
        }));
        return;
      }
      const isAlarm =
        msg.alarm !== undefined ||
        severity === 'error' ||
        /alarm|fault|error|告警|故障/.test(eventCode);
      if (isAlarm) {
        const entry = String(msg.alarm ?? msg.alarmCode ?? msg.code ?? msg.eventCode ?? msg.event ?? `${deviceId}:alarm`);
        set((s) => ({
          deviceMap: {
            ...s.deviceMap,
            [deviceId]: {
              ...(cur || emptyDevice(deviceId)),
              deviceId,
              online: cur?.online ?? true,
              timestamp: now,
              lastTopic: topic,
              alarmList: Array.from(new Set([...(cur?.alarmList || []), entry])),
              points: { ...(cur?.points || {}), ...points },
            },
          },
        }));
      } else {
        get().upsertDevicePoints(deviceId, points, { lastTopic: topic });
      }
      return;
    }

    // 其余 topic（device:log / response / config-* / bind-status / …）：并入点位，保证记录存在
    if (Object.keys(points).length > 0 || !cur) {
      get().upsertDevicePoints(deviceId, points, {
        lastTopic: topic,
        online: cur?.online ?? onlineFromMessage(msg),
      });
    }
  },

  reset: () =>
    set({ deviceMap: {}, selectedDeviceId: null, selectedDeviceFocus: true, deviceActionTick: 0, aiEvents: [] }),
}));